// Keeps a cart line honest: its option must be one ITS product really has, and its price must be that product's real
// price. A line used to be able to carry another product's option (the product page kept a shopper's pick when they moved
// to a different product) — a ₹999 ring then went into the bag at the ₹200 price of an option it never had.
//
// `row` below = the product as the store serves it now: { price, variations, hasAdvanced }.
export const normName = (s) => String(s == null ? "" : s).trim().toLowerCase();
const num = (x) => { const n = Number(x); return Number.isFinite(n) ? n : null; };
const namedList = (a) => (Array.isArray(a) ? a.filter((v) => v && v.name) : []);

// The simple option a cart line names (not an advanced colour/size combo).
export const optionOf = (line) => (line && (line.selectedVariation?.name || line.selectedOption || line.variation_name)) || null;
const isAdvancedLine = (line, row) => !!(line && (line.selectedCombo || (line.selectedAdvanced && Object.keys(line.selectedAdvanced).length))) || !!(row && row.hasAdvanced);

// The product's OWN option matching a selection, or null. Use this when adding to the bag, so whatever was selected —
// even a leftover from another product — can only ever resolve to an option this product really offers.
export function ownVariation(product, selected) {
  const name = selected && selected.name;
  if (!name || !product) return null;
  return namedList(product.variations).find((v) => normName(v.name) === normName(name)) || null;
}

// The price one unit must cost: the option's own price when it has one, else the product's price. null = can't tell.
export function unitPriceFor(row, line) {
  const opt = optionOf(line);
  if (opt) {
    const v = namedList(row && row.variations).find((x) => normName(x.name) === normName(opt));
    const p = v ? num(v.price) : null;
    if (p != null && p > 0) return p;
  }
  return num(row && row.price);
}

const lineKey = (l) => l.cartKey || l.id;

// Reconcile a whole cart against the live product rows ({ [productId]: row }).
// Returns { cart, notices }. `cart` is the SAME array when nothing needed changing (safe inside an effect).
//   • option the product doesn't have, product has NO options  → option stripped, price = the product's price   ("fixed")
//   • option the product doesn't have, product HAS options     → line removed (we can't know what was meant)    ("removed")
//   • valid option or plain line, wrong price                  → price corrected                                ("price")
// Lines whose product has no live row, advanced (colour/size) lines, and lines needing an option are left to the server.
export function reconcileCart(cart, rowsById) {
  if (!Array.isArray(cart) || !rowsById) return { cart, notices: [] };
  const notices = [];
  const out = [];
  let changed = false;
  for (const line of cart) {
    const row = line && rowsById[line.id];
    if (!row || isAdvancedLine(line, row)) { out.push(line); continue; }
    const opt = optionOf(line);
    const hasOptions = namedList(row.variations).length > 0;
    const match = opt ? namedList(row.variations).find((v) => normName(v.name) === normName(opt)) : null;
    const name = line.name || "An item";

    if (opt && !match) {
      changed = true;
      if (hasOptions) { notices.push({ type: "removed", name, option: String(opt) }); continue; }
      const price = num(row.price);
      const { selectedVariation, selectedOption, variation_name, ...rest } = line; // eslint-disable-line no-unused-vars
      const fixed = { ...rest, selectedVariation: null, ...(price != null ? { price } : {}), cartKey: line.id };
      notices.push({ type: "fixed", name, option: String(opt), from: num(line.price), to: price });
      out.push(fixed);
      continue;
    }
    const expected = unitPriceFor(row, line);
    const paid = num(line.price);
    if (expected != null && paid != null && Math.abs(expected - paid) > 0.5) {
      changed = true;
      notices.push({ type: "price", name, from: paid, to: expected });
      out.push({ ...line, price: expected, ...(match && line.selectedVariation ? { selectedVariation: { ...line.selectedVariation, price: num(match.price) ?? line.selectedVariation.price } } : {}) });
      continue;
    }
    out.push(line);
  }
  if (!changed) return { cart, notices: [] };
  // A repaired line may now match another line of the same product — keep one line per product + option.
  const seen = new Map(); const merged = [];
  for (const l of out) {
    const k = [l.id, normName(optionOf(l)), l.selectedCombo || "", l.custom_answer || "", l.custom_image || ""].join("|");
    if (seen.has(k)) { const i = seen.get(k); merged[i] = { ...merged[i], qty: (Number(merged[i].qty) || 1) + (Number(l.qty) || 1) }; }
    else { seen.set(k, merged.length); merged.push(l); }
  }
  return { cart: merged, notices };
}

export function noticeText(n) {
  if (n.type === "removed") return `"${n.name}" was removed from your bag — the option "${n.option}" isn't available on it. Add it again from its page.`;
  if (n.type === "fixed")   return `"${n.name}" was corrected${n.to != null ? ` — its price is ₹${n.to}` : ""}.`;
  return `The price of "${n.name}" changed from ₹${n.from} to ₹${n.to}.`;
}
